/**
 * @file Lists the organizations the current identity can switch to.
 */

import { getConfig } from '@components/Config';
import { useAppContext } from '@components/AppContext';
import { type AppContext } from '@components/AppContext/types';

type Organization = {
  id: string;
  name: string;
  isDisabled?: boolean;
};

const consoleOrganizationId = getConfig('consoleOrganizationId');
const pinnedOrganizationId = getConfig('PINNED_ORG_ID');

/**
 * Lists the identity's organizations, sorted by name, without the console organization or a disabled one.
 * The pinned organization of the environment comes first when the identity has access to it.
 */
const useOrganizations = (): Organization[] => {
  const { identity } = useAppContext() as AppContext;
  const organizations = identity?.organizations || [];

  const noConsoleOrganizations = organizations
    .filter(({ id }: Organization) => id !== consoleOrganizationId)
    .filter(({ isDisabled }: Organization) => !isDisabled);

  const sortedOrganizations = noConsoleOrganizations.sort(
    (organizationA: Organization, organizationB: Organization) => (
      organizationA.name.localeCompare(organizationB.name)
    ),
  );

  const pinnedOrganizations = sortedOrganizations
    .filter(({ id }: Organization) => id === pinnedOrganizationId);
  const otherOrganizations = sortedOrganizations
    .filter(({ id }: Organization) => id !== pinnedOrganizationId);

  return [ ...pinnedOrganizations, ...otherOrganizations ];
};

export default useOrganizations;
